import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Volume2, Trash2, RotateCcw, Square } from "lucide-react";
import { useVoice } from "@/context/VoiceContext";
import { chat as chatCfg } from "@/config";
import { speak, stopAllSpeech } from "@/lib/speech";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = "bca_chat_v2";

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; }
};
const newSession = () => ({ sessionId: crypto.randomUUID(), messages: [] });

export default function AskAndrew() {
  const { chatOpen, setChatOpen } = useVoice();
  const [state, setState] = useState(() => load() || newSession());
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null); // last user message that failed
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const speakerRef = useRef(null);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(state)); }, [state]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [state.messages, busy]);
  useEffect(() => {
    if (chatOpen) setTimeout(() => inputRef.current?.focus(), 250);
    const onKey = (e) => e.key === "Escape" && setChatOpen(false);
    if (chatOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chatOpen, setChatOpen]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setFailed(null);
    setBusy(true);
    setState((s) => ({ ...s, messages: [...s.messages, { role: "user", text: msg }] }));
    let reply = "";
    let started = false;
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: state.sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const ev = JSON.parse(line.slice(5));
          if (ev.error) throw new Error(ev.error);
          if (ev.delta) {
            reply += ev.delta;
            if (!started) {
              started = true;
              setState((s) => ({ ...s, messages: [...s.messages, { role: "assistant", text: reply }] }));
            } else {
              setState((s) => {
                const m = [...s.messages];
                m[m.length - 1] = { role: "assistant", text: reply };
                return { ...s, messages: m };
              });
            }
          }
        }
      }
      if (!started) throw new Error("empty");
    } catch {
      setFailed(msg);
      setState((s) => ({ ...s, messages: s.messages.filter((m, i) => !(i === s.messages.length - 1 && m.role === "user" && m.text === msg)) }));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => { stopAllSpeech(); setSpeakingIdx(null); setState(newSession()); setFailed(null); };

  const toggleSpeak = (i, text) => {
    if (speakingIdx === i) { speakerRef.current?.stop(); setSpeakingIdx(null); return; }
    speakerRef.current?.stop();
    setSpeakingIdx(i);
    speakerRef.current = speak(text, { lang: "en-IN", onEnd: () => setSpeakingIdx(null), onError: () => setSpeakingIdx(null) });
  };

  const empty = state.messages.length === 0;

  return (
    <AnimatePresence>
      {chatOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setChatOpen(false)}
            className="fixed inset-0 z-[48] bg-black/50 lg:bg-black/30"
          />
          <motion.aside
            key="drawer"
            data-testid="chat-drawer"
            role="dialog" aria-label="Ask Andrew"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 32 }}
            className="glass fixed inset-y-0 right-0 z-[49] flex w-full flex-col border-l border-cream/10 lg:w-[440px]"
          >
            <header className="flex items-center justify-between border-b border-cream/10 px-5 py-4">
              <div>
                <h3 className="font-serif text-xl text-cream">{chatCfg.title}</h3>
                <p className="text-xs text-gray2">{chatCfg.subtitle}</p>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid="chat-clear" onClick={clear} aria-label="Clear chat" title="Clear chat" className="grid h-9 w-9 place-items-center rounded-full text-gray2 transition-colors hover:bg-cream/5 hover:text-cream">
                  <Trash2 className="h-4 w-4" />
                </button>
                <button data-testid="chat-close" onClick={() => setChatOpen(false)} aria-label="Close chat" className="grid h-9 w-9 place-items-center rounded-full text-gray2 transition-colors hover:bg-cream/5 hover:text-cream">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </header>

            <div ref={listRef} data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
              {empty && (
                <div className="pt-6">
                  <p className="font-serif text-2xl leading-snug text-cream/90">Ask about my cases, my stack, or when I can start.</p>
                  <p className="mt-2 text-sm text-gray2">I answer only from what's on this site.</p>
                </div>
              )}
              {state.messages.map((m, i) => (
                <div key={i} data-testid={`chat-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-sm bg-oxblood text-cream" : "rounded-bl-sm border border-cream/10 bg-cream/[0.04] text-cream/90"}`}>
                    {m.text}
                    {m.role === "assistant" && (
                      <button
                        data-testid="chat-speak"
                        onClick={() => toggleSpeak(i, m.text)}
                        aria-label={speakingIdx === i ? "Stop reading" : "Read aloud"}
                        className={`mt-2 flex items-center gap-1 text-[0.68rem] transition-colors ${speakingIdx === i ? "text-gold" : "text-gray2 hover:text-gold"}`}
                      >
                        {speakingIdx === i ? <Square className="h-3 w-3 fill-current" /> : <Volume2 className="h-3.5 w-3.5" />}
                        {speakingIdx === i ? "Stop" : "Listen"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div data-testid="chat-typing" className="flex justify-start">
                  <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm border border-cream/10 bg-cream/[0.04] px-4 py-3">
                    <span className="dot h-1.5 w-1.5 rounded-full bg-gold" />
                    <span className="dot h-1.5 w-1.5 rounded-full bg-gold" style={{ animationDelay: "0.2s" }} />
                    <span className="dot h-1.5 w-1.5 rounded-full bg-gold" style={{ animationDelay: "0.4s" }} />
                  </div>
                </div>
              )}
              {failed && (
                <div data-testid="chat-error" className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-200">
                  <span>Objection — the line dropped. Your message wasn't sent.</span>
                  <button data-testid="chat-retry" onClick={() => send(failed)} className="flex shrink-0 items-center gap-1 text-gold hover:underline">
                    <RotateCcw className="h-3.5 w-3.5" /> Retry
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-cream/10 px-5 pb-5 pt-3">
              {empty && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {chatCfg.chips.map((c) => (
                    <button key={c} data-testid="chat-chip" onClick={() => send(c)} className="chip !text-[0.72rem] !normal-case !tracking-normal text-cream/80 transition-colors hover:border-gold/60 hover:text-gold">
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 rounded-full border border-cream/15 bg-noir/60 py-1 pl-4 pr-1 focus-within:border-gold/60">
                <input
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={chatCfg.placeholder}
                  className="flex-1 bg-transparent text-sm text-cream outline-none placeholder:text-gray2/70"
                />
                <button type="submit" data-testid="chat-send" disabled={busy || !input.trim()} aria-label="Send" className="grid h-10 w-10 place-items-center rounded-full bg-oxblood text-cream transition-opacity disabled:opacity-40">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
